import { Component, OnInit, ViewChild, inject, signal, computed, DestroyRef } from '@angular/core';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { of } from 'rxjs';
import { concatMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';

import { ToastrService } from 'ngx-toastr';

import { SourceTournament } from '../../models/sourceTournament';
import { ImportService } from '../../services/import/import.service';
import {
  ImportModalComponent,
  ImportOptions,
} from '../../components/import-modal/import-modal.component';
import { Tournament } from '../../models';
import { FormsModule } from '@angular/forms';
import { PlayerService, TournamentService } from '../../services';
import { FilteringService } from '../../services/shared/filtering/filtering.service';
import { LoggingService } from '../../services/shared/logging/logging.service';
import { TournamentListComponent } from '../../components/tournament/tournament-list/tournament-list.component';
import { TournamentImportListComponent } from '../../components/tournament/tournament-import-list/tournament-import-list.component';
import { exportToCSV } from '../../utils/csv-export.utils';

@Component({
  selector: 'app-tournaments',
  standalone: true,
  imports: [FormsModule, ImportModalComponent, TournamentListComponent, TournamentImportListComponent],
  templateUrl: './tournaments.component.html',
  styleUrls: ['./tournaments.component.scss'],
})
export class TournamentsComponent implements OnInit {
  @ViewChild(ImportModalComponent) importModal!: ImportModalComponent;

  private readonly tournamentService = inject(TournamentService);
  private readonly importService = inject(ImportService);
  private readonly toasterService = inject(ToastrService);
  private readonly playerService = inject(PlayerService);
  private readonly filteringService = inject(FilteringService);
  private readonly loggingService = inject(LoggingService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  // Reactive state using signals
  selectedSourceTournament = signal<SourceTournament | null>(null);
  searchText = signal('');
  tournaments = signal<Tournament[]>([]);
  sourceTournaments = signal<SourceTournament[]>([]);
  isImporting = signal<boolean>(false);

  // Computed filtered lists
  filteredTournaments = computed(() =>
    this.filteringService.filterItems(
      this.tournaments(),
      this.searchText(),
      ['name', 'city', 'number']
    )
  );

  filteredSourceTournaments = computed(() =>
    this.filteringService.filterItems(
      this.sourceTournaments(),
      this.searchText(),
      ['name', 'location', 'tournamentId']
    )
  );

  // Tab state
  activeTab = signal<'local' | 'import'>('local');

  ngOnInit() {
    this.getTournamentsAndSourceTournaments();
  }

  filterTournaments() {
    // No longer needed - computed properties handle filtering automatically
  }

  clearSearch() {
    this.searchText.set('');
  }

  setActiveTab(tab: 'local' | 'import'): void {
    this.activeTab.set(tab);
  }

  getTournamentsAndSourceTournaments(): void {
    this.tournamentService.getAll()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((tournaments) => {
        this.tournaments.set(tournaments);
        if (tournaments.length === 0) {
          this.activeTab.set('import');
        }
        this.importService
          .getSourceTournamentsEndpoint('LPGA')
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((sourceTournaments) => {
            const enrichedSourceTournaments = sourceTournaments.map(
              (sourceTournament) => ({
                ...sourceTournament,
                exists: tournaments.some(
                  (t) => t.importSourceId === sourceTournament.tournamentId
                ),
                id: tournaments.find(
                  (t) => t.importSourceId === sourceTournament.tournamentId
                )?.tournamentId,
              })
            );
            this.sourceTournaments.set(enrichedSourceTournaments);
          });
      });
  }

  importEmptyTournament() {
    const $importEmpty = this.importService.importEmptyTournament(0);
    $importEmpty.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        const selectedTournament = this.selectedSourceTournament();
        this.toasterService.success(
          `Tournament ${selectedTournament?.name} imported successfully!`,
          'Success'
        );
        this.resetAndRefresh();
      },
    });
  }

  private importSelectedTournament(importOptions: ImportOptions): void {
    const selectedTournament = this.selectedSourceTournament();
    const tournamentId = selectedTournament?.tournamentId;
    const tournamentName = selectedTournament?.name;

    if (!tournamentId) {
      this.toasterService.error('No Tournament selected for import.');
      return;
    }

    // Set importing flag to disable the import button
    this.isImporting.set(true);

    let pointsRaceImportResult: { playersCreated?: number; pointsCreated?: number } | null = null;

    const importPointRaceRankings$ = importOptions.includePointsRaceRankings
      ? this.importService.importPointsRaceRankings(
        tournamentId,
        'LPGA',
        importOptions.pointsRaceName
      ).pipe(tap((result: any) => { pointsRaceImportResult = result; }))
      : of(null);

    const importMissingPointsRacePlayers$ =
      importOptions.includePointsRaceRankings
        ? this.importService.importMissingPointsRacePlayers(
          tournamentId,
          'LPGA'
        )
        : of(null);

    const importTournament$ = this.importService.importTournament(
      tournamentId,
      'LPGA',
      importOptions.includeCourses,
      importOptions.includePlayers
    );

    importTournament$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        concatMap(() => importPointRaceRankings$),
        concatMap(() => importMissingPointsRacePlayers$)
      )
      .subscribe({
        next: () => {
          let successMessage: string;
          if (importOptions.includePointsRaceRankings && pointsRaceImportResult) {
            const players = pointsRaceImportResult.playersCreated ?? 0;
            const points = pointsRaceImportResult.pointsCreated ?? 0;
            successMessage = `Tournament ${tournamentName} imported successfully, including ${players} Points Race player rankings and ${points} point schedule entries!`;
          } else if (importOptions.includePointsRaceRankings) {
            successMessage = `Tournament ${tournamentName} imported successfully, including Points Race Rankings!`;
          } else {
            successMessage = `Tournament ${tournamentName} imported successfully!`;
          }

          this.toasterService.success(successMessage, 'Success');
          this.isImporting.set(false);
          this.resetAndRefresh();
        },
        error: (error) => {
          this.loggingService.error(
            `Error importing tournament: ${error.message}`,
            'TournamentsComponent'
          );
          this.toasterService.error(
            `Error importing tournament: ${error.message}`,
            'Failure'
          );
          this.isImporting.set(false);
        },
      });
  }

  private resetAndRefresh(): void {
    this.selectedSourceTournament.set(null);
    this.getTournamentsAndSourceTournaments();

    // Trigger navtop to refresh and select the newly imported active tournament
    this.refreshNavtop();
  }

  private refreshNavtop(): void {
    // Fetch all tournaments to find the newly active one
    this.tournamentService.getAll()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (tournaments) => {
          const activeTournament = tournaments.find(t => t.active);
          if (activeTournament) {
            // Set the active tournament in the service, which will trigger navtop to update
            this.tournamentService.selectedTournament = activeTournament;
            this.loggingService.debug(
              'TournamentsComponent',
              `Set active tournament in navtop: ${activeTournament.name}`
            );
          }
        },
        error: (error) => {
          this.loggingService.error(
            'TournamentsComponent',
            'Error refreshing navtop after import',
            error
          );
        },
      });
  }

  toggleTournamentStatus(tournament: Tournament): void {
    this.tournamentService
      .markTournamentActive(tournament.tournamentId!)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.toasterService.success(`${tournament.name} marked active`, 'Success');
          this.getTournamentsAndSourceTournaments();

          // Refresh navtop to select the newly active tournament and its active round
          this.refreshNavtop();
        },
        error: (error) => {
          this.loggingService.error(
            `Error marking ${tournament.name} active: ${error.message}`,
            'TournamentsComponent'
          );
          this.toasterService.error(
            `Error marking ${tournament.name} active: ${error.message}`,
            'Failure'
          );
        },
      });
  }

  openImportModal(t: SourceTournament): void {
    this.selectedSourceTournament.set(t);
    this.importModal.openModal(t, this.isImporting);
  }

  onImportConfirmed($event: ImportOptions): void {
    if (this.selectedSourceTournament()) {
      this.importSelectedTournament($event);
    }
  }

  viewTournament(tournament: Tournament): void {
    // Set the tournament in the service, which will trigger navtop to update
    this.tournamentService.selectedTournament = tournament;
    this.loggingService.debug(
      'TournamentsComponent',
      `Selected tournament in navtop: ${tournament.name}`
    );

    // Navigate to the tournament detail page
    this.router.navigate(['/tournaments', tournament.tournamentId]);
  }

  exportLocalTournaments(): void {
    const tournaments = this.filteredTournaments();
    
    if (tournaments.length === 0) {
      this.toasterService.warning('No tournaments to export', 'Export');
      return;
    }

    const columns = [
      { key: 'importSourceId' as const, label: 'Source ID' },
      { key: 'number' as const, label: 'Tournament Number' },
      { key: 'name' as const, label: 'Tournament Name' },
      { key: 'city' as const, label: 'City' },
      { key: 'stateCode' as const, label: 'State' },
      { key: 'countryCode' as const, label: 'Country' },
      { key: 'startDate' as const, label: 'Start Date' },
      { key: 'endDate' as const, label: 'End Date' },
      { key: 'coursesCount' as const, label: 'Courses' },
      { key: 'roundsCount' as const, label: 'Rounds' },
      { key: 'playersCount' as const, label: 'Players' },
      { key: 'purse' as const, label: 'Purse' },
      { key: 'active' as const, label: 'Active' }
    ];

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
    const fileName = `tournaments_local_${timestamp}`;

    try {
      exportToCSV(tournaments, fileName, columns);
      this.toasterService.success(`Exported ${tournaments.length} tournaments`, 'Export Successful');
      this.loggingService.debug('TournamentsComponent', `Exported ${tournaments.length} local tournaments`);
    } catch (error: any) {
      this.toasterService.error('Failed to export tournaments', 'Export Failed');
      this.loggingService.error('TournamentsComponent', 'Error exporting local tournaments', error);
    }
  }

  exportSourceTournaments(): void {
    const sourceTournaments = this.filteredSourceTournaments();
    
    if (sourceTournaments.length === 0) {
      this.toasterService.warning('No source tournaments to export', 'Export');
      return;
    }

    const columns = [
      { key: 'tournamentId' as const, label: 'Tournament ID' },
      { key: 'name' as const, label: 'Tournament Name' },
      { key: 'location' as const, label: 'Location' },
      { key: 'startDate' as const, label: 'Start Date' },
      { key: 'endDate' as const, label: 'End Date' },
      { key: 'coursesCount' as const, label: 'Courses' },
      { key: 'roundsCount' as const, label: 'Rounds' },
      { key: 'playersCount' as const, label: 'Players' },
      { key: 'exists' as const, label: 'Exists in Local System' }
    ];

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
    const fileName = `tournaments_lpga_hq_${timestamp}`;

    try {
      exportToCSV(sourceTournaments, fileName, columns);
      this.toasterService.success(`Exported ${sourceTournaments.length} source tournaments`, 'Export Successful');
      this.loggingService.debug('TournamentsComponent', `Exported ${sourceTournaments.length} source tournaments`);
    } catch (error: any) {
      this.toasterService.error('Failed to export source tournaments', 'Export Failed');
      this.loggingService.error('TournamentsComponent', 'Error exporting source tournaments', error);
    }
  }
}
